import { ComponentType, LazyExoticComponent, lazy } from 'react';

// A route chunk can fail to load when a deploy replaced the bundle under an open
// tab, or on a flaky connection. Instead of surfacing a ChunkLoadError to the
// Error Boundary (and a client error report), retry the import once and then
// reload the page once. The reload marker lives in sessionStorage with a short
// TTL, so a chunk that is genuinely broken cannot put the tab into a reload loop.
const RELOAD_MARKER_KEY = 'vv.chunkReloadAt';
const RELOAD_MARKER_TTL_MS = 60_000;

export const isChunkLoadError = (error: unknown): boolean => {
    const name = (error as { name?: unknown } | null | undefined)?.name;
    const message = String((error as { message?: unknown } | null | undefined)?.message ?? '');
    return (
        name === 'ChunkLoadError' ||
        /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
        /Failed to fetch dynamically imported module/i.test(message) ||
        /Importing a module script failed/i.test(message)
    );
};

const readReloadMarker = (): number | null => {
    try {
        const raw = window.sessionStorage.getItem(RELOAD_MARKER_KEY);
        return raw ? Number(raw) : null;
    } catch {
        return null;
    }
};

const writeReloadMarker = (now: number): void => {
    try {
        window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(now));
    } catch {
        // Storage unavailable (private mode) — the loop guard is best effort.
    }
};

export interface LoadWithRetryOptions {
    reload?: () => void;
    now?: () => number;
}

export const loadWithRetry = async <TModule>(
    factory: () => Promise<TModule>,
    options: LoadWithRetryOptions = {}
): Promise<TModule> => {
    const reload = options.reload ?? (() => window.location.reload());
    const now = options.now ?? (() => Date.now());

    try {
        return await factory();
    } catch (firstError) {
        if (!isChunkLoadError(firstError)) {
            throw firstError;
        }
    }

    try {
        return await factory();
    } catch (secondError) {
        if (!isChunkLoadError(secondError)) {
            throw secondError;
        }
        const lastReloadAt = readReloadMarker();
        const reloadedRecently =
            lastReloadAt !== null && now() - lastReloadAt < RELOAD_MARKER_TTL_MS;
        if (reloadedRecently) {
            throw secondError;
        }
        writeReloadMarker(now());
        reload();
        // The page is navigating away; keep the import pending so Suspense keeps
        // showing its fallback instead of handing the error to the Error Boundary.
        return new Promise<TModule>(() => {});
    }
};

export const lazyWithRetry = <TComponent extends ComponentType<any>>(
    factory: () => Promise<{ default: TComponent }>
): LazyExoticComponent<TComponent> => lazy(() => loadWithRetry(factory));
